<template>
  <article
    :id="props.blok.type"
    v-editable="props.blok"
    class="document white"
    :class="{ loading: props.loading }"
  >
    <header>
      <h3 class="h6">{{ props.blok.heading }}</h3>
    </header>
    <figure>
      <figcaption v-if="props.blok.text" class="text-s">
        {{ props.blok.text }}
      </figcaption>
      <div class="thumbnail">
        <img
          :src="`${props.blok.thumbnail.filename}/m/0x300`"
          :alt="props.blok.thumbnail.alt"
          :title="props.blok.thumbnail.title"
          :data-copyright="props.blok.thumbnail.copyright || null"
          loading="lazy"
        />
        <div class="loader">
          <PflegeSpinner color-fg="transparent" :size="3" />
          <Transition name="slider" mode="out-in">
            <p :key="loadingText" class="text-s text-bold">{{ loadingText }}</p>
          </Transition>
        </div>
      </div>
    </figure>
    <footer>
      <PflegeButton
        v-if="props.blok.linkText"
        variant="primary"
        :label="props.blok.linkText"
        :icon="isDownloaded ? 'check-line' : props.blok.linkIcon || 'search-line'"
        :disabled="props.loading"
        :title="props.loading ? 'Dokument wird geladen' : null"
        :href="documentLink"
        target="_blank"
        @click.left="documentClick"
        @click.middle="documentClick"
        @click.right="documentClick"
      />

      <div
        v-if="props.blok.signDocument"
        class="toggle-wrapper"
        :class="{ success: documentData.dokumentSigniert }"
      >
        <Toggle
          :label="
            documentData.dokumentSigniert ? 'Unterschrift übernommen' : 'Unterschrift übernehmen'
          "
          :model-value="documentData.dokumentSigniert"
          :has-error="
            (!!props.errors.signature || !!props.errors.documents?.[props.blok.type]) &&
            showErrorMessages
          "
          :error-message="
            !!props.errors.signature
              ? 'Erstellen Sie zuerst die Unterschrift, um diese übernehmen zu können.'
              : 'Nutzen Sie den Schieberegler, um Ihre Unterschrift zu übernehmen.'
          "
          @update:model-value="updateSignatureToggle"
        />
      </div>

      <div class="opt-ins">
        <DocumentsViewOptIn
          v-for="optIn in props.blok.optIns"
          :key="optIn._uid"
          v-model="documentData.optIns[optIn.type]"
          :blok="optIn"
          :has-error="!!props.errors.optIns?.[optIn.type] && showErrorMessages"
          @input="updateOptIn"
        />
      </div>
    </footer>
  </article>
</template>

<script setup>
import { computed, shallowRef } from 'vue'
import Toggle from '../../input/Toggle.vue'
import DocumentsViewOptIn from './DocumentsViewOptIn.vue'
import { useWizcoStore } from '../../../stores/wizco'

const props = defineProps({
  blok: {
    type: Object,
    required: true
  },
  loading: {
    type: Boolean,
    default: false
  },
  showErrorMessages: {
    type: Boolean,
    default: false
  },
  link: {
    type: String,
    default: ''
  },
  errors: {
    type: Object,
    required: false,
    default: () => {}
  }
})

const emit = defineEmits(['documentClick', 'updateDocumentData'])

const wizco = useWizcoStore()
const documentData = computed(() =>
  wizco.stepData[wizco.activeStepUuid].data.dokumente?.find(
    (document) => document.type === props.blok.type
  )
)
if (!documentData.value.optIns) {
  documentData.value.optIns = {}
}

const updateSignatureToggle = (val) => {
  documentData.value.dokumentSigniert = val
  emit('updateDocumentData')
}

const updateOptIn = (type, val) => {
  documentData.value.optIns[type] = val
  if (!val) {
    delete documentData.value.optIns[type]
  }
  emit('updateDocumentData')
}

const loadingText = shallowRef('')
if (props.loading) {
  loadingText.value = ' ' // special empty char to prevent initial jumping
  setTimeout(() => {
    loadingText.value = 'Ihre Angaben werden übernommen.'
  }, 500)
  setTimeout(() => {
    loadingText.value = 'Bitte haben Sie noch etwas Geduld.'
  }, 4700)
  setTimeout(() => {
    loadingText.value = 'Ihr Antrag ist gleich verfügbar.'
  }, 8900)
}

const documentLink = computed(() =>
  props.link ? `${props.link}?download=true&name=${encodeURIComponent(props.blok.heading)}` : null
)
const isDownloaded = shallowRef(false)
const documentClick = () => {
  isDownloaded.value = true
  emit('documentClick')
}
</script>

<style scoped lang="scss">
.document {
  --document-default-spacing: 0.5rem;
  --document-default-border-radius: 0.44rem;

  border-radius: var(--document-default-border-radius);
  padding: 1rem;
}

.document footer {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;

  :deep(.base-button) {
    justify-content: center;
  }
}

.document figure {
  margin: 0;
}
.document figure figcaption {
  margin-top: var(--document-default-spacing);
}
.document figure .thumbnail {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--document-default-border-radius);
  padding: var(--document-default-spacing);
  height: 10.44rem;
  margin-block: 1rem;
  background-color: var(--mint-lighter);
  position: relative;
  overflow: clip;
  transition: background-color var(--time1) ease-in-out;
}
.document figure .thumbnail img {
  max-height: 100%;
  max-width: 100%;
  transition: filter var(--time2) ease-in-out;
}

/* loading state */
.document figure .thumbnail .loader {
  position: absolute;
  text-align: center;
  opacity: 0;
  scale: 0;
  transition-property: opacity, scale;
  transition-duration: var(--time1);
  transition-timing-function: ease-in-out;
}
.document figure .thumbnail .loader .spinner {
  margin: 0;
  margin-block-end: 0.5rem;
  display: inline-block;
}
.document.loading figure .thumbnail .loader {
  opacity: 1;
  scale: 1;
  @starting-style {
    opacity: 0;
    scale: 0;
  }
}
.document.loading figure .thumbnail {
  background-color: var(--mint-lightest);
  @starting-style {
    background-color: var(--mint-lighter);
  }
}
.document.loading figure .thumbnail img {
  filter: blur(4px);
  @starting-style {
    filter: blur(0);
  }
}
.slider-enter-active,
.slider-leave-active {
  transition-property: opacity, translate;
  transition-duration: var(--time2);
}
.slider-enter-from,
.slider-leave-to {
  opacity: 0;
}
.slider-enter-from {
  translate: 50% 0;
}
.slider-leave-to {
  translate: -50% 0;
}
@media (prefers-reduced-motion) {
  .slider-enter-from,
  .slider-leave-to {
    translate: 0;
  }
}

.toggle-wrapper {
  background-color: var(--warning-light);
  padding: 0.25rem 0;
  width: 100%;
}

.toggle-wrapper.success {
  background-color: var(--success-light);
}

.opt-ins {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
}
</style>
